<template>
  <div class="app-shell" :class="{ 'app-shell--collapsed': sidebarCollapsed }">
    <aside class="app-sidebar" :class="{ 'is-open': mobileOpen }" aria-label="Workspace navigation">
      <div class="app-sidebar__brand-row">
        <router-link to="/" class="app-sidebar__brand" :aria-label="tx('返回 PaperNova 首页', 'Back to the PaperNova home page')">
          <span class="app-sidebar__logo-wrap">
            <img src="@/assets/papernova-logo.svg" alt="PaperNova" class="app-sidebar__logo" />
          </span>
        </router-link>

        <button
          class="app-sidebar__collapse"
          type="button"
          :aria-label="sidebarCollapsed ? tx('展开侧边栏', 'Expand sidebar') : tx('收起侧边栏', 'Collapse sidebar')"
          @click="toggleSidebar"
        >
          <Icon :name="sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'" :size="18" />
        </button>
      </div>

      <div class="app-sidebar__sections">
        <section v-for="group in navGroups" :key="group.key" class="app-sidebar__section">
          <div class="app-sidebar__section-label">{{ group.label }}</div>
          <nav class="app-sidebar__nav" :aria-label="group.label">
            <router-link
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              class="app-sidebar__nav-link"
              :class="{ 'is-primary': group.key === 'research' }"
              active-class="is-active"
              :title="sidebarCollapsed ? navLabel(item) : navHint(item)"
              @click="mobileOpen = false"
            >
              <span class="app-sidebar__nav-icon">
                <Icon :name="item.icon" :size="18" />
              </span>
              <span class="app-sidebar__nav-label">{{ navLabel(item) }}</span>
              <span
                v-if="item.to === '/tasks' && activeTaskCount"
                class="app-sidebar__task-count"
                :aria-label="$t('tasks.journal.activeCount', { count: activeTaskCount })"
              >{{ activeTaskCount > 9 ? '9+' : activeTaskCount }}</span>
            </router-link>
          </nav>
          <router-link
            v-if="group.key === 'research' && ownedLines.length"
            to="/#packages"
            class="app-sidebar__more"
            :title="tx('开通其他品类', 'Explore other packages')"
          >
            <Icon name="Plus" :size="14" />
            <span>{{ tx('开通其他品类', 'Explore other packages') }}</span>
          </router-link>
        </section>
      </div>

      <div class="app-sidebar__footer">
        <router-link to="/profile" class="app-sidebar__account" :title="username">
          <span class="app-sidebar__avatar">{{ avatarLetter }}</span>
          <span class="app-sidebar__account-copy">
            <span class="app-sidebar__account-name">{{ username || 'Account' }}</span>
            <span class="app-sidebar__account-meta">{{ ownedSummary }}</span>
          </span>
          <span v-if="creditsAvailable !== null" class="app-sidebar__credits">
            <strong>{{ creditsAvailable.toLocaleString() }}</strong>
            <small>{{ tx('积分', 'credits') }}</small>
          </span>
        </router-link>
        <router-link to="/demo" class="app-sidebar__demo">
          <Icon name="CirclePlay" :size="14" />
          <span>{{ tx('观看操作演示', 'Watch the walkthrough') }}</span>
        </router-link>
      </div>
    </aside>

    <button
      v-if="mobileOpen"
      class="app-shell__mobile-backdrop"
      type="button"
      :aria-label="tx('关闭导航', 'Close navigation')"
      @click="mobileOpen = false"
    ></button>

    <div class="app-shell__workspace">
      <header class="app-topbar">
        <div class="app-topbar__left">
          <button
            class="app-topbar__menu-toggle"
            type="button"
            :aria-label="mobileOpen ? tx('关闭菜单', 'Close menu') : tx('打开菜单', 'Open menu')"
            @click="mobileOpen = !mobileOpen"
          >
            <Icon :name="mobileOpen ? 'X' : 'Menu'" :size="21" />
            <span v-if="activeTaskCount" class="app-topbar__task-count">{{ activeTaskCount > 9 ? '9+' : activeTaskCount }}</span>
          </button>

          <div class="app-topbar__context">
            <span class="app-topbar__eyebrow">{{ tx('研究工作台', 'Research workspace') }}</span>
            <span class="app-topbar__divider" aria-hidden="true"></span>
            <strong>{{ currentPageTitle }}</strong>
          </div>
        </div>

        <div class="app-topbar__right">
          <LanguageSelector />
          <div class="user-menu" :class="{ 'is-open': userMenuOpen }">
            <button
              class="user-menu__trigger"
              type="button"
              :aria-expanded="userMenuOpen"
              aria-haspopup="true"
              @click="toggleUserMenu"
            >
              <span class="user-menu__avatar">{{ avatarLetter }}</span>
              <span class="user-menu__identity">
                <span class="user-menu__name">{{ username || 'Account' }}</span>
                <span class="user-menu__role">{{ tx('研究者账户', 'Researcher account') }}</span>
              </span>
              <Icon name="ChevronDown" :size="14" class="user-menu__caret" />
            </button>

            <transition name="menu-fade">
              <div v-if="userMenuOpen" class="user-menu__dropdown" role="menu" @click.stop>
                <div class="user-menu__header">
                  <div class="user-menu__header-name">{{ username }}</div>
                  <div class="user-menu__header-email">{{ userEmail }}</div>
                </div>
                <div class="user-menu__items">
                  <button class="user-menu__item" type="button" @click="go('/profile')">
                    <Icon name="Settings" :size="16" />
                    <span>{{ $t('userProfile.dropdown.profileSettings') }}</span>
                  </button>
                  <button class="user-menu__item" type="button" @click="go('/account/credits')">
                    <Icon name="Coins" :size="16" />
                    <span>{{ tx('学术积分明细', 'Academic credit history') }}</span>
                  </button>
                  <div class="user-menu__sep"></div>
                  <button class="user-menu__item user-menu__item--danger" type="button" @click="logout">
                    <Icon name="LogOut" :size="16" />
                    <span>{{ $t('userProfile.dropdown.logout') }}</span>
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </header>

      <div v-if="userMenuOpen" class="app-shell__menu-backdrop" @click="userMenuOpen = false"></div>

      <main class="app-shell__main">
        <slot />
      </main>
    </div>
  </div>
</template>

<script>
import Icon from '@/shared/ui/Icon.vue';
import authService from '@/services/auth.js';
import logger from '@/services/logger';
import taskService from '@/services/researchTaskService.js';
import { isTaskActive } from '@/shared/tasks/taskStatus.js';
import LanguageSelector from '@/features/localization/components/LanguageSelector.vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

const OWNED_ORDER_STATUSES = ['observing', 'settled', 'refunding'];

export default {
  name: 'AppShell',
  components: { Icon, LanguageSelector },
  data() {
    return {
      userMenuOpen: false,
      mobileOpen: false,
      sidebarCollapsed: false,
      username: '',
      userEmail: '',
      ownedLines: [],
      creditsAvailable: null,
      tasks: taskService.getAllTasks(),
      taskEventHandlers: [],
    };
  },
  computed: {
    activeTaskCount() {
      return this.tasks.filter((task) => (
        taskService.isJournalTask(task)
        && isTaskActive(task)
      )).length;
    },
    avatarLetter() {
      const n = (this.username || 'U').trim();
      return n.charAt(0).toUpperCase() || 'U';
    },
    researchItems() {
      const owned = new Set(this.ownedLines);
      const showAll = !owned.size;
      const items = [];
      if (showAll || owned.has('journal')) {
        items.push({ to: '/projects', icon: 'PanelsTopLeft', i18n: 'navigation.projects', label: 'Journal & conference papers', hintI18n: 'navigation.projectsHint' });
      } else if (owned.has('humanities')) {
        items.push({ to: '/projects', icon: 'Landmark', label: this.tx('文科论文项目', 'Humanities papers'), hint: this.tx('管理文科期刊论文项目与研究资料', 'Manage humanities paper projects') });
      }
      if (showAll || ['undergraduate', 'master', 'doctoral'].some(line => owned.has(line))) {
        items.push({ to: '/theses', icon: 'GraduationCap', i18n: 'navigation.thesisPlans', label: 'Degree thesis', hintI18n: 'navigation.thesisPlansHint' });
      }
      return items;
    },
    navGroups() {
      return [
        {
          key: 'research',
          label: this.ownedLines.length ? this.tx('我的研究', 'My research') : this.$t('navigation.researchCenter'),
          items: this.researchItems,
        },
        {
          key: 'tools',
          label: this.$t('navigation.researchTools'),
          items: [
            { to: '/literature-reviews', icon: 'BookOpenText', i18n: 'navigation.topicLiteratureReview', label: 'Topic literature review', hintI18n: 'navigation.topicLiteratureReviewHint' },
            { to: '/tools', icon: 'Wrench', label: this.tx('单项工具', 'Single tools'), hint: this.tx('文献、选题与写作检查', 'Literature, ideation, and writing checks') },
          ],
        },
        {
          key: 'mine',
          label: this.tx('我的', 'Mine'),
          items: [
            { to: '/library', icon: 'Library', i18n: 'references.title', label: 'Research library', hint: this.tx('按文献与文件夹管理全部资料', 'Manage all papers and folders') },
            { to: '/tasks', icon: 'ListChecks', i18n: 'navigation.runHistory', label: 'Runs', hintI18n: 'navigation.tasksHint' },
            { to: '/account/credits', icon: 'Coins', i18n: 'navigation.academicCredits', label: 'Academic credits', hintI18n: 'navigation.academicCreditsHint' },
          ],
        },
      ];
    },
    navItems() {
      return this.navGroups.flatMap(group => group.items);
    },
    ownedSummary() {
      const names = {
        journal: this.tx('期刊会议论文', 'Journal papers'),
        humanities: this.tx('文科论文', 'Humanities papers'),
        undergraduate: this.tx('本科学位论文', 'Bachelor thesis'),
        master: this.tx('硕士学位论文', 'Master thesis'),
        doctoral: this.tx('博士学位论文', 'Doctoral thesis'),
      };
      if (!this.ownedLines.length) return this.tx('尚未开通研究品类', 'No package yet');
      return this.ownedLines.map(line => names[line]).join(' · ');
    },
    currentPageTitle() {
      if (this.$route.query.thesis_plan_id && (this.$route.path.startsWith('/task/') || this.$route.path.startsWith('/run/'))) return this.tx('研究计划', 'Research plans');
      if (this.$route.query.thesis_plan_id && this.$route.path.startsWith('/projects/')) return this.tx('研究计划', 'Research plans');
      if (this.$route.query.sop === '1' && (this.$route.path.startsWith('/task/') || this.$route.path.startsWith('/run/'))) return this.tx('期刊会议论文 SOP', 'Flagship SOP');
      const match = this.navItems.find(item => this.$route.path.startsWith(item.to));
      if (match) return this.navLabel(match);

      const detailTitles = [
        { prefix: '/profile', label: this.tx('个人设置', 'Profile settings') },
        { prefix: '/payment', label: this.tx('购买积分', 'Buy credits') },
        { prefix: '/account/credits', label: this.tx('学术积分', 'Academic credits') },
      ];
      const detail = detailTitles.find(item => this.$route.path.startsWith(item.prefix));
      return detail ? detail.label : 'PaperNova';
    },
  },
  mounted() {
    const sidebarState = window.localStorage.getItem('papernova-sidebar-collapsed');
    this.sidebarCollapsed = sidebarState === 'true';
    this.loadUser();
    this.loadAccount();
    const refreshTasks = () => { this.tasks = taskService.getAllTasks(); };
    ['task:created', 'task:updated', 'task:completed', 'task:failed', 'tasks:refreshed'].forEach((event) => {
      taskService.on(event, refreshTasks);
      this.taskEventHandlers.push([event, refreshTasks]);
    });
    taskService.syncFromServer({ activeOnly: true }).catch((error) => {
      logger.warn('AppShell', 'Unable to refresh run history', error?.message);
    });
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    this.taskEventHandlers.forEach(([event, handler]) => taskService.off(event, handler));
    document.removeEventListener('keydown', this.onKeydown);
  },
  watch: {
    $route(to, from) {
      this.userMenuOpen = false;
      this.mobileOpen = false;
      if (to.path !== from.path) this.loadAccount();
    },
  },
  methods: {
    tx(zh, en) {
      return this.$i18n.locale === 'en' ? en : zh;
    },
    navLabel(item) {
      return item.i18n && this.$te(item.i18n) ? this.$t(item.i18n) : item.label;
    },
    navHint(item) {
      if (item.hint) return item.hint;
      return item.hintI18n && this.$te(item.hintI18n) ? this.$t(item.hintI18n) : '';
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      window.localStorage.setItem('papernova-sidebar-collapsed', String(this.sidebarCollapsed));
    },
    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen;
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.userMenuOpen = false;
        this.mobileOpen = false;
      }
    },
    go(path) {
      this.userMenuOpen = false;
      if (this.$route.path !== path) this.$router.push(path);
    },
    async logout() {
      this.userMenuOpen = false;
      try {
        await authService.logout();
      } catch (err) {
        logger.error('AppShell', 'Logout error:', err);
      } finally {
        this.$router.push('/');
      }
    },
    async loadAccount() {
      if (!authService.isAuthenticated()) return;
      const headers = { Authorization: `Bearer ${authService.getToken()}` };
      try {
        const [orders, balance] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/orders`, { headers }),
          axios.get(`${API_BASE_URL}/api/credits/balance`, { headers }),
        ]);
        const lines = orders.data.orders
          .filter(order => order.kind === 'package' && OWNED_ORDER_STATUSES.includes(order.status))
          .map(order => order.line);
        this.ownedLines = [...new Set(lines)];
        this.creditsAvailable = balance.data.available;
      } catch (err) {
        logger.error('AppShell', 'loadAccount error:', err);
      }
    },
    async loadUser() {
      if (!authService.isAuthenticated()) return;
      try {
        const profile = await authService.getUserProfile();
        if (profile) {
          this.username = profile.username || '';
          this.userEmail = profile.email || '';
        }
      } catch (err) {
        logger.error('AppShell', 'loadUser error:', err);
      }
    },
  },
};
</script>

<style scoped>
.app-shell {
  --sidebar-width: 292px;
  min-height: 100vh;
  display: flex;
  background: var(--shell-canvas);
}

.app-shell--collapsed { --sidebar-width: 84px; }

.app-sidebar {
  position: sticky;
  top: 0;
  z-index: calc(var(--z-sticky) + 1);
  width: var(--sidebar-width);
  height: 100vh;
  flex: 0 0 var(--sidebar-width);
  display: flex;
  flex-direction: column;
  padding: 18px 14px 16px;
  color: var(--sidebar-text);
  background: var(--sidebar-surface);
  border-right: 1px solid var(--sidebar-border);
  transition: width var(--transition-slow), flex-basis var(--transition-slow);
  overflow: hidden;
}

.app-sidebar::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: linear-gradient(180deg, var(--sidebar-accent) 0%, var(--sidebar-accent) 35%, transparent 78%);
}

.app-sidebar__brand-row {
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 18px;
}

.app-sidebar__brand {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 11px;
  color: var(--sidebar-text-strong);
  text-decoration: none;
}

.app-sidebar__brand:hover { color: var(--sidebar-text-strong); text-decoration: none; }

.app-sidebar__logo-wrap {
  width: 172px;
  height: 42px;
  flex: 0 0 172px;
  display: grid;
  place-items: center;
  padding: 0;
}

.app-sidebar__logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.96;
  transition: opacity var(--transition-fast);
}

.app-sidebar__brand:hover .app-sidebar__logo { opacity: 1; }

.app-sidebar__collapse {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: grid;
  place-items: center;
  padding: 0;
  color: var(--sidebar-text-muted);
  background: transparent;
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.app-sidebar__collapse:hover { color: var(--sidebar-text-strong); background: var(--sidebar-surface-hover); }

.app-sidebar__sections {
  min-height: 0;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--sidebar-scroll-thumb) transparent;
}

.app-sidebar__section + .app-sidebar__section { margin-top: 18px; }

.app-sidebar__section-label {
  margin: 0 11px 9px;
  color: var(--sidebar-text-muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}

.app-sidebar__nav { display: flex; flex-direction: column; gap: 2px; }

.app-sidebar__nav-link {
  position: relative;
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px;
  color: var(--sidebar-text);
  border-radius: 10px;
  text-decoration: none;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.app-sidebar__nav-link:hover {
  color: var(--sidebar-text-strong);
  background: var(--sidebar-surface-hover);
  text-decoration: none;
}

.app-sidebar__nav-link.is-active {
  color: var(--sidebar-text-strong);
  background: var(--sidebar-surface-active);
  box-shadow: inset 0 0 0 1px var(--sidebar-accent-soft), 0 7px 18px var(--sidebar-shadow);
}

.app-sidebar__nav-link.is-active::before {
  content: '';
  position: absolute;
  left: -14px;
  top: 9px;
  bottom: 9px;
  width: 3px;
  background: var(--sidebar-accent);
  border-radius: 0 3px 3px 0;
}

.app-sidebar__nav-icon {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  display: grid;
  place-items: center;
  border-radius: 7px;
}

.app-sidebar__nav-link.is-primary { min-height: 46px; }
.app-sidebar__nav-link.is-primary .app-sidebar__nav-icon { width: 32px; height: 32px; flex-basis: 32px; background: var(--sidebar-icon-surface); }
.app-sidebar__nav-link.is-primary .app-sidebar__nav-label { font-size: 14px; font-weight: 650; }

.app-sidebar__nav-label { min-width: 0; flex: 1; overflow: hidden; font-size: 13.5px; font-weight: 500; line-height: 1.25; text-overflow: ellipsis; white-space: nowrap; }
.app-sidebar__more {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 0;
  padding: 6px 10px 6px 16px;
  color: var(--sidebar-text-muted);
  border-radius: 8px;
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;
}
.app-sidebar__more:hover { color: var(--sidebar-text-strong); background: var(--sidebar-surface-hover); text-decoration: none; }
.app-sidebar__task-count {
  min-width: 20px;
  height: 20px;
  display: inline-grid;
  place-items: center;
  padding: 0 5px;
  color: var(--primary-dark);
  background: var(--sidebar-accent);
  border: 2px solid var(--sidebar-surface);
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
}

.app-sidebar__footer {
  padding-top: 12px;
  border-top: 1px solid var(--sidebar-border);
  white-space: nowrap;
}

.app-sidebar__account {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  color: var(--sidebar-text-strong);
  background: var(--sidebar-surface-hover);
  border-radius: 10px;
  text-decoration: none;
}
.app-sidebar__account:hover { color: var(--sidebar-text-strong); background: var(--sidebar-surface-active); text-decoration: none; }
.app-sidebar__avatar { width: 34px; height: 34px; flex: 0 0 34px; display: grid; place-items: center; color: var(--primary-dark); background: var(--sidebar-accent); border-radius: 9px; font-size: 14px; font-weight: 800; }
.app-sidebar__account-copy { min-width: 0; flex: 1; display: flex; flex-direction: column; line-height: 1.2; }
.app-sidebar__account-name { overflow: hidden; font-size: 13px; font-weight: 650; text-overflow: ellipsis; }
.app-sidebar__account-meta { margin-top: 3px; overflow: hidden; color: var(--sidebar-text-muted); font-size: 10.5px; text-overflow: ellipsis; }
.app-sidebar__credits { display: flex; flex-direction: column; align-items: flex-end; line-height: 1.15; }
.app-sidebar__credits strong { color: var(--sidebar-accent); font-size: 14px; font-weight: 750; }
.app-sidebar__credits small { color: var(--sidebar-text-muted); font-size: 10px; }
.app-sidebar__demo { display: flex; align-items: center; gap: 7px; margin-top: 8px; padding: 4px 10px; color: var(--sidebar-text-faint); font-size: 11px; text-decoration: none; }
.app-sidebar__demo:hover { color: var(--sidebar-text-strong); text-decoration: none; }

.app-shell--collapsed .app-sidebar__section-label,
.app-shell--collapsed .app-sidebar__nav-label,
.app-shell--collapsed .app-sidebar__more span,
.app-shell--collapsed .app-sidebar__account-copy,
.app-shell--collapsed .app-sidebar__credits,
.app-shell--collapsed .app-sidebar__demo span { display: none; }
.app-shell--collapsed .app-sidebar__more,
.app-shell--collapsed .app-sidebar__demo { justify-content: center; padding-inline: 0; }
.app-shell--collapsed .app-sidebar__account { justify-content: center; padding: 6px; background: transparent; }

.app-shell--collapsed .app-sidebar__brand-row { flex-direction: column; min-height: 84px; margin-bottom: 12px; }
.app-shell--collapsed .app-sidebar__logo-wrap { width: 56px; height: 40px; flex-basis: 40px; padding: 0; }
.app-shell--collapsed .app-sidebar__nav-link { justify-content: center; padding: 7px; }
.app-shell--collapsed .app-sidebar__task-count {
  position: absolute;
  top: 3px;
  right: 3px;
}

.app-shell__workspace { min-width: 0; flex: 1 1 auto; display: flex; flex-direction: column; }

.app-topbar {
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
  min-height: var(--shell-nav-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 0 28px;
  background: var(--topbar-surface);
  border-bottom: 1px solid var(--border);
  backdrop-filter: blur(12px);
}

.app-topbar__left,
.app-topbar__right,
.app-topbar__context { display: flex; align-items: center; }
.app-topbar__left { min-width: 0; }
.app-topbar__right { gap: 12px; flex-shrink: 0; }
.app-topbar__context { min-width: 0; gap: 10px; }
.app-topbar__eyebrow { color: var(--medium-gray); font-size: 12px; font-weight: 600; letter-spacing: 0.04em; }
.app-topbar__divider { width: 1px; height: 16px; background: var(--border); }
.app-topbar__context strong { overflow: hidden; color: var(--primary); font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }

.app-topbar__menu-toggle {
  position: relative;
  display: none;
  width: 38px;
  height: 38px;
  margin-right: 12px;
  padding: 0;
  place-items: center;
  color: var(--dark-gray);
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 9px;
  cursor: pointer;
}
.app-topbar__task-count {
  position: absolute;
  top: -5px;
  right: -6px;
  min-width: 17px;
  height: 17px;
  display: grid;
  place-items: center;
  padding: 0 4px;
  color: var(--white);
  background: var(--info-dark);
  border: 2px solid var(--white);
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
}

.user-menu { position: relative; }
.user-menu__trigger {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 5px 8px 5px 5px;
  color: var(--dark-gray);
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}
.user-menu__trigger:hover,
.user-menu.is-open .user-menu__trigger { border-color: var(--border-strong); box-shadow: var(--shadow-xs); }
.user-menu__avatar { width: 32px; height: 32px; display: grid; place-items: center; color: var(--white); background: var(--primary); border-radius: 8px; font-size: 13px; font-weight: 700; }
.user-menu__identity { max-width: 130px; display: flex; flex-direction: column; align-items: flex-start; line-height: 1.15; }
.user-menu__name { width: 100%; overflow: hidden; color: var(--dark); font-size: 12px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
.user-menu__role { margin-top: 3px; color: var(--medium-gray); font-size: 10px; }
.user-menu__caret { color: var(--medium-gray); }

.user-menu__dropdown { position: absolute; top: calc(100% + 9px); right: 0; z-index: var(--z-dropdown); min-width: 260px; overflow: hidden; background: var(--white); border: 1px solid var(--border); border-radius: 11px; box-shadow: var(--shadow-lg); }
.user-menu__header { padding: 13px 15px; background: var(--surface-alt); border-bottom: 1px solid var(--border-light); }
.user-menu__header-name { overflow: hidden; color: var(--dark); font-size: 13px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
.user-menu__header-email { margin-top: 3px; overflow: hidden; color: var(--medium-gray); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.user-menu__items { padding: 5px; }
.user-menu__item { width: 100%; display: flex; align-items: center; gap: 11px; padding: 9px 10px; color: var(--dark-gray); background: none; border: 0; border-radius: 7px; font-family: inherit; font-size: 13px; text-align: left; cursor: pointer; }
.user-menu__item:hover { color: var(--primary); background: var(--light-gray); }
.user-menu__item--danger:hover { color: var(--accent-dark); background: var(--danger-surface); }
.user-menu__sep { height: 1px; margin: 4px 0; background: var(--border-light); }

.app-shell__menu-backdrop { position: fixed; inset: 0; z-index: calc(var(--z-dropdown) - 1); background: transparent; }
.app-shell__main { min-width: 0; flex: 1 1 auto; }
.app-shell__mobile-backdrop { display: none; }

.menu-fade-enter-active,
.menu-fade-leave-active { transition: opacity var(--transition-fast), transform var(--transition-fast); }
.menu-fade-enter-from,
.menu-fade-leave-to { opacity: 0; transform: translateY(-5px); }

@media (max-width: 1080px) {
  .app-shell { --sidebar-width: 84px; }
  .app-sidebar__brand-row { flex-direction: column; min-height: 84px; margin-bottom: 12px; }
  .app-sidebar__logo-wrap { width: 56px; height: 40px; flex-basis: 40px; padding: 0; }
  .app-sidebar__section-label,
  .app-sidebar__nav-label,
  .app-sidebar__more span,
  .app-sidebar__account-copy,
  .app-sidebar__credits,
  .app-sidebar__demo span { display: none; }
  .app-sidebar__nav-link { justify-content: center; padding-inline: 7px; }
  .app-sidebar__more,
  .app-sidebar__demo { justify-content: center; padding-inline: 0; }
  .app-sidebar__account { justify-content: center; padding: 6px; background: transparent; }
  .app-sidebar__task-count { position: absolute; top: 3px; right: 3px; }
}

@media (max-width: 760px) {
  .app-shell { display: block; }
  .app-sidebar {
    position: fixed;
    left: 0;
    width: min(84vw, 292px);
    transform: translateX(-102%);
    transition: transform var(--transition-base);
    box-shadow: 18px 0 40px var(--sidebar-mobile-shadow);
  }
  .app-sidebar.is-open { transform: translateX(0); }
  .app-sidebar__brand-row { min-height: 46px; flex-direction: row; margin-bottom: 18px; }
  .app-sidebar__logo-wrap { width: 172px; height: 42px; flex-basis: 172px; padding: 0; }
  .app-sidebar__section-label,
  .app-sidebar__nav-label,
  .app-sidebar__more span,
  .app-sidebar__demo span { display: block; }
  .app-sidebar__account-copy,
  .app-sidebar__credits { display: flex; }
  .app-sidebar__nav-link { justify-content: flex-start; padding-inline: 10px; }
  .app-sidebar__more { justify-content: flex-start; padding-inline: 16px 10px; }
  .app-sidebar__demo { justify-content: flex-start; padding-inline: 10px; }
  .app-sidebar__account { justify-content: flex-start; padding: 9px 10px; background: var(--sidebar-surface-hover); }
  .app-sidebar__task-count,
  .app-shell--collapsed .app-sidebar__task-count { position: static; }
  .app-sidebar__collapse { display: none; }
  .app-shell__mobile-backdrop { position: fixed; inset: 0; z-index: var(--z-sticky); display: block; width: 100%; height: 100%; padding: 0; background: var(--overlay-modal); border: 0; }
  .app-topbar { min-height: 62px; padding: 0 16px; }
  .app-topbar__menu-toggle { display: grid; }
  .app-topbar__eyebrow,
  .app-topbar__divider { display: none; }
  .user-menu__identity { display: none; }
}

@media (max-width: 480px) {
  .app-topbar__right { gap: 7px; }
  .user-menu__caret { display: none; }
  .user-menu__trigger { padding-right: 5px; }
}

@media (prefers-reduced-motion: reduce) {
  .app-sidebar,
  .app-sidebar__nav-link,
  .menu-fade-enter-active,
  .menu-fade-leave-active { transition-duration: 0.01ms; }
}
</style>
